import * as Updates from "expo-updates";

export interface UpdateInfo {
  isAvailable: boolean;
  isUpdatePending: boolean;
  isUpdateLoading: boolean;
  manifest?: any;
  error?: string;
  isDevelopment?: boolean;
}

class UpdateService {
  private updateInfo: UpdateInfo = {
    isAvailable: false,
    isUpdatePending: false,
    isUpdateLoading: false,
    isDevelopment: false,
  };

  /**
   * Check if we're in a development build
   */
  private isDevelopmentBuild(): boolean {
    // Check if we're in development mode
    return __DEV__;
  }

  /**
   * Check for available updates
   */
  async checkForUpdates(): Promise<UpdateInfo> {
    try {
      // In development builds, simulate update checking
      if (this.isDevelopmentBuild()) {
        this.updateInfo.isDevelopment = true;
        this.updateInfo.isAvailable = false;
        this.updateInfo.error = "Updates not available in development builds";
        console.log("Development build detected - simulating update check");
        return this.updateInfo;
      }

      if (!Updates.isEnabled) {
        this.updateInfo.error = "Updates are not enabled";
        return this.updateInfo;
      }

      const update = await Updates.checkForUpdateAsync();

      if (update.isAvailable) {
        this.updateInfo.isAvailable = true;
        this.updateInfo.manifest = update.manifest;
        console.log("Update available:", update.manifest);
      } else {
        this.updateInfo.isAvailable = false;
        console.log("No updates available");
      }

      return this.updateInfo;
    } catch (error) {
      this.updateInfo.error = `Failed to check for updates: ${error}`;
      console.error("Error checking for updates:", error);
      return this.updateInfo;
    }
  }

  /**
   * Download and apply the update
   */
  async applyUpdate(): Promise<boolean> {
    try {
      // In development builds, simulate update application
      if (this.isDevelopmentBuild()) {
        console.log(
          "Development build detected - simulating update application",
        );
        return false;
      }

      if (!this.updateInfo.isAvailable) {
        console.log("No update available to apply");
        return false;
      }

      this.updateInfo.isUpdateLoading = true;

      // Download the update
      const result = await Updates.fetchUpdateAsync();

      if (result.isNew) {
        // Apply the update
        await Updates.reloadAsync();
        return true;
      } else {
        console.log("Update was not new, no reload needed");
        return false;
      }
    } catch (error) {
      this.updateInfo.error = `Failed to apply update: ${error}`;
      console.error("Error applying update:", error);
      return false;
    } finally {
      this.updateInfo.isUpdateLoading = false;
    }
  }

  /**
   * Get current update info
   */
  getUpdateInfo(): UpdateInfo {
    return { ...this.updateInfo };
  }

  /**
   * Check if updates are enabled
   */
  isUpdatesEnabled(): boolean {
    return Updates.isEnabled;
  }

  /**
   * Get the current channel
   */
  getChannel(): string {
    return Updates.channel || "default";
  }

  /**
   * Get the current runtime version
   */
  getRuntimeVersion(): string {
    return Updates.runtimeVersion || "unknown";
  }

  /**
   * Get the current update ID
   */
  getUpdateId(): string {
    return Updates.updateId || "unknown";
  }

  /**
   * Check if the app is running from an update
   */
  isRunningFromUpdate(): boolean {
    return Updates.isEmbeddedLaunch;
  }

  /**
   * Get the current build type
   */
  getBuildType(): string {
    if (this.isDevelopmentBuild()) {
      return "development";
    }
    return "production";
  }

  /**
   * Get the last update check time
   */
  getLastUpdateCheckTime(): Date {
    return new Date();
  }
}

export const updateService = new UpdateService();
export default updateService;
